/* global globalThis */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { apiFetch } from '../../utils/api';
import './ScormStats.css';
const safeGlobal = typeof globalThis !== 'undefined' ? globalThis : window;
const ScormStats = ({ course, onClose }) => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    if (course?.scorm_course_id) {
      fetchStats();
    }
  }, [course?.scorm_course_id]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const data = await apiFetch(
        `${window._env_.API_BASE_URL}/admin/scorm/courses/${course.scorm_course_id}/stats`
      );
      setStats(data || []);
    } catch (error) {
      console.error('Error fetching course stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredStats = () =>
    stats.filter(stat => {
      const name = stat.user_name || '';
      const email = stat.email || '';

      const matchesSearch =
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        email.toLowerCase().includes(searchTerm.toLowerCase());

      if (!matchesSearch) {
        return false;
      }

      switch (filter) {
        case 'completed':
          return stat.completion_status === 'completed';
        case 'passed':
          return stat.success_status === 'passed';
        case 'in-progress':
          return stat.completion_status === 'incomplete';
        case 'not-started':
          return stat.completion_status === 'not attempted';
        default:
          return true;
      }
    });

  const calculateSummary = () => {
    const total = stats.length;
    const completed = stats.filter(s => s.completion_status === 'completed').length;
    const passed = stats.filter(s => s.success_status === 'passed').length;
    const inProgress = stats.filter(s => s.completion_status === 'incomplete').length;

    return { total, completed, passed, inProgress };
  };

  const summary = calculateSummary();
  const filteredStats = getFilteredStats();

  const maximizeTitle = isMaximized ? 'Restore' : 'Maximize';

  return (
    <div className={`scorm-stats-overlay ${isMaximized ? 'maximized' : ''}`}>
      <div className="scorm-stats-modal">
        <div className="scorm-stats-header">
          <div>
            <h2>{course.title} - Statistics</h2>
            <p className="stats-subtitle">Course Performance Report</p>
          </div>
          <div className="header-actions">
            <button
              className="window-btn"
              onClick={() => setIsMaximized(prev => !prev)}
              title={maximizeTitle}
            >
              {isMaximized ? '🗗' : '🗖'}
            </button>
            <button className="close-btn" onClick={onClose}>
              &times;
            </button>
          </div>
        </div>

        <div className="stats-summary-cards">
          <div className="stat-card total">
            <h3>Total Enrolled</h3>
            <span className="stat-value">{summary.total}</span>
          </div>

          <div className="stat-card completed">
            <h3>Completed</h3>
            <span className="stat-value">{summary.completed}</span>
            <span className="stat-percent">
              {summary.total > 0
                ? Math.round((summary.completed / summary.total) * 100)
                : 0}
              %
            </span>
          </div>

          <div className="stat-card passed">
            <h3>Passed</h3>
            <span className="stat-value">{summary.passed}</span>
            <span className="stat-percent">
              {summary.total > 0
                ? Math.round((summary.passed / summary.total) * 100)
                : 0}
              %
            </span>
          </div>

          <div className="stat-card stat-progress">
            <h3>In Progress</h3>
            <span className="stat-value">{summary.inProgress}</span>
          </div>
        </div>

        <div className="stats-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-tabs">
            {['all', 'completed', 'passed', 'in-progress'].map(tab => (
              <button
                key={tab}
                className={filter === tab ? 'active' : ''}
                onClick={() => setFilter(tab)}
              >
                {tab.replace('-', ' ')}
              </button>
            ))}
          </div>
        </div>

        <div className="stats-table-container">
          {loading ? (
            <div className="loading-stats">Loading statistics...</div>
          ) : filteredStats.length === 0 ? (
            <div className="no-stats">
              No users found matching current filters
            </div>
          ) : (
            <table className="stats-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Department</th>
                  <th>Status</th>
                  <th>Score</th>
                  <th>Attempts</th>
                  <th>Last Accessed</th>
                </tr>
              </thead>
              <tbody>
                {filteredStats.map(stat => {
                  const hasScore = stat.score_raw !== null;
                  const passed =
                    hasScore && stat.score_raw >= course.passing_score;

                  return (
                    <tr key={stat.user_id}>
                      <td>
                        <div className="user-cell">
                          <span className="user-name">
                            {stat.user_name || 'Unknown User'}
                          </span>
                          <span className="user-email">
                            {stat.email || 'No Email'}
                          </span>
                        </div>
                      </td>

                      <td>{stat.department || '-'}</td>

                      <td>
                        <span
                          className={`status-pill ${stat.completion_status.replace(
                            ' ',
                            '-'
                          )}`}
                        >
                          {stat.completion_status}
                        </span>

                        {stat.success_status !== 'unknown' && (
                          <span
                            className={`status-pill ${stat.success_status} success-pill`}
                          >
                            {stat.success_status}
                          </span>
                        )}
                      </td>

                      <td className="score-cell">
                        {hasScore ? (
                          <span
                            className={passed ? 'pass-score' : 'fail-score'}
                          >
                            {stat.score_raw}%
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>

                      <td>{stat.attempt_number}</td>

                      <td>
                        {stat.last_accessed_at
                          ? new Date(
                            stat.last_accessed_at
                          ).toLocaleDateString()
                          : 'Never'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- Sonar-required prop validation ---------- */

ScormStats.propTypes = {
  course: PropTypes.shape({
    scorm_course_id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    passing_score: PropTypes.number.isRequired
  }).isRequired,
  onClose: PropTypes.func.isRequired
};

export default ScormStats;
